import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { ArrowRight, Sprout, Factory, GraduationCap, FlaskConical, Users, Leaf, ShieldCheck, Trophy } from 'lucide-react';
import { FadeUp, Stagger, StaggerItem } from '../components/Motion';
import Counter from '../components/Counter';
import CertBadge from '../components/CertBadge';
import api from '../lib/api';
import { resolveImg } from '../lib/img';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/4qyup5r0_IMG_7097.jpeg';

export default function Home() {
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [formations, setFormations] = useState([]);
  const [stats, setStats] = useState([]);
  const [featured, setFeatured] = useState(null);
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';

  useEffect(() => {
    api.get('/products').then((r) => setProducts(r.data)).catch(() => {});
    api.get('/formations').then((r) => setFormations(r.data.slice(0, 3))).catch(() => {});
    api.get('/stats').then((r) => setStats(r.data)).catch(() => {});
    // Featured banner: prefer Marthe TOE prize article, else latest article
    api.get('/articles').then((r) => {
      const arts = r.data || [];
      const marthe = arts.find((a) => /marthe\s*toe/i.test(a.title_fr || ''));
      setFeatured(marthe || arts[0] || null);
    }).catch(() => {});
  }, []);

  const domains = [
    { Icon: Sprout, k: 0, img: 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/byggk5ef_IMG_7084.jpeg' },
    { Icon: Factory, k: 1, img: 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/qaldt8ae_IMG_7078.jpeg' },
    { Icon: GraduationCap, k: 2, img: 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/3yw249cq_IMG_7085.jpeg' },
    { Icon: FlaskConical, k: 3, img: 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/x3q6q38m_IMG_7082.jpeg' },
    { Icon: Users, k: 4, img: 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/f4phi0a7_IMG_7102.jpeg' },
    { Icon: Leaf, k: 5, img: 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/wlaur6fq_IMG_7083.jpeg' },
  ];
  const items = t('activities.items', { returnObjects: true });

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Lawatan landscape" className="w-full h-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
        </div>
        <div className="relative container-x max-w-7xl mx-auto pb-24 lg:pb-32 pt-32 grid gap-10 lg:grid-cols-12 items-end">
          <div className="lg:col-span-8">
            <FadeUp>
              <p className="overline text-sun mb-6" data-testid="hero-overline">Centre de Permaculture · Burkina Faso</p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-bone leading-[0.95] tracking-tight whitespace-pre-line text-balance" data-testid="hero-title">
                {t('home.hero_title')}
              </h1>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-8 text-lg lg:text-xl text-bone/85 max-w-2xl leading-relaxed" data-testid="hero-sub">
                {t('home.hero_sub')}
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/about" data-testid="cta-discover" className="btn-accent">
                  {t('home.cta_discover')} <ArrowRight size={18} />
                </Link>
                <Link to="/activities" data-testid="cta-activities" className="inline-flex items-center gap-2 text-bone border-b border-bone/40 pb-1 hover:border-sun hover:text-sun transition-colors px-1 py-2">
                  {t('home.cta_activities')} <ArrowRight size={16} />
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
        <div className="absolute right-6 bottom-8 hidden lg:flex flex-col items-end gap-3 text-bone/70 text-xs font-mono uppercase tracking-[0.3em]">
          <span>Bobo-Dioulasso</span>
          <span>11°10&apos;N · 4°17&apos;W</span>
        </div>
      </section>

      {/* INTRO */}
      <section className="section bg-bone grain">
        <div className="container-x max-w-7xl mx-auto grid gap-16 lg:grid-cols-12 items-start">
          <FadeUp className="lg:col-span-4">
            <p className="overline mb-4">{t('home.intro_overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-ink leading-[1.05] tracking-tight" data-testid="intro-title">
              {t('home.intro_title')}
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <p className="text-lg text-ink-soft leading-relaxed">
              {t('home.intro_text')}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <CertBadge name="ECOCERT" size={84} />
              <CertBadge name="BioSPG" size={84} />
              <CertBadge name="CNABio" size={84} />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FEATURED — Prix Yacouba Sawadogo 2026 */}
      {featured && (
        <section data-testid="featured-banner" className="bg-forest text-bone py-20 sm:py-24">
          <div className="container-x max-w-7xl mx-auto">
            <FadeUp>
              <div className="inline-flex items-center gap-2 bg-sun text-ink text-xs font-bold uppercase tracking-widest rounded-full px-4 py-2 mb-6">
                <Trophy size={14} /> {t('home.featured_overline')}
              </div>
            </FadeUp>
            <div className="grid gap-10 lg:grid-cols-12 items-center">
              <FadeUp className="lg:col-span-6" delay={0.05}>
                <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={resolveImg(featured.cover_image)}
                    alt={featured[`title_${lng}`]}
                    className="w-full h-full object-cover"
                    data-testid="featured-image"
                  />
                </div>
              </FadeUp>
              <FadeUp className="lg:col-span-6" delay={0.15}>
                <h2
                  className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-bone text-balance"
                  data-testid="featured-title"
                >
                  {featured[`title_${lng}`]}
                </h2>
                <p className="mt-6 text-bone/85 text-lg leading-relaxed">
                  {featured[`excerpt_${lng}`]}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/news"
                    className="btn-primary bg-sun text-ink hover:bg-sun/90"
                    data-testid="featured-cta"
                  >
                    {t('home.featured_read')} <ArrowRight size={16} />
                  </Link>
                </div>
              </FadeUp>
            </div>
          </div>
        </section>
      )}

      {/* DOMAINS */}
      <section className="section bg-sand/40">
        <div className="container-x max-w-7xl mx-auto">
          <div className="grid gap-10 lg:grid-cols-12 items-end mb-16">
            <FadeUp className="lg:col-span-7">
              <p className="overline mb-3">{t('home.domains_overline')}</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight">
                {t('home.domains_title')}
              </h2>
            </FadeUp>
            <FadeUp delay={0.1} className="lg:col-span-4 lg:col-start-9 text-ink-soft">
              {t('activities.intro')}
            </FadeUp>
          </div>
          <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {domains.map(({ Icon, k, img }) => (
              <StaggerItem key={k}>
                <div className="group relative rounded-3xl overflow-hidden hover-lift h-full min-h-[360px]" data-testid={`domain-card-${k}`}>
                  <img src={img} alt={items[k]?.t} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/55 to-ink/15" />
                  <div className="relative h-full p-7 flex flex-col justify-end text-bone">
                    <div className="w-12 h-12 grid place-items-center rounded-2xl bg-bone/15 backdrop-blur text-bone mb-4 border border-bone/20">
                      <Icon size={22} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-heading text-2xl text-bone leading-tight">{items[k]?.t}</h3>
                    <p className="text-bone/80 leading-relaxed mt-2 text-sm">{items[k]?.d}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section className="section bg-forest text-bone relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/syufiggb_IMG_7103.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative container-x max-w-7xl mx-auto">
          <FadeUp>
            <p className="overline text-sun mb-3">{t('home.impact_overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight max-w-3xl">{t('home.impact_title')}</h2>
          </FadeUp>
          <Stagger className="mt-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            {(stats.length ? stats : [
              { key: 'years', value: 19, suffix: '+', label_fr: "Années d'expérience", label_en: 'Years of experience' },
              { key: 'tons', value: 48, suffix: 't', label_fr: 'Tonnes récoltées', label_en: 'Tons harvested' },
              { key: 'certifications', value: 2, suffix: '', label_fr: 'Certifications', label_en: 'Certifications' },
              { key: 'beneficiaries', value: 60, suffix: '+', label_fr: 'Bénéficiaires', label_en: 'Beneficiaries' },
            ]).map((it) => (
              <StaggerItem key={it.key}>
                <div className="border-l border-bone/20 pl-6">
                  <div className="font-heading text-6xl lg:text-7xl text-sun" data-testid={`stat-${it.key}`}>
                    <Counter value={it.value} suffix={it.suffix || ''} />
                  </div>
                  <div className="mt-3 text-sm uppercase tracking-widest text-bone/80">{it[`label_${lng}`] || it.label_fr}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="section bg-bone">
        <div className="container-x max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <p className="overline mb-3">{t('home.products_overline')}</p>
              <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t('home.products_title')}</h2>
            </div>
            <Link to="/products" data-testid="cta-products" className="btn-outline">
              {t('common.view_all')} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {products.slice(0, 2).map((p) => (
              <FadeUp key={p.id}>
                <Link to="/products" className="group block bg-white rounded-3xl overflow-hidden border border-border-soft hover-lift" data-testid={`product-card-${p.id}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={resolveImg(p.image)} alt={p[`name_${lng}`]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8">
                    <div className="flex gap-2 mb-3">
                      {p.certifications?.map((c) => (
                        <span key={c} className="text-[10px] uppercase tracking-widest font-bold text-forest border border-forest/30 rounded-full px-2 py-0.5">{c}</span>
                      ))}
                    </div>
                    <h3 className="font-heading text-3xl text-ink mb-2">{p[`name_${lng}`]}</h3>
                    <p className="text-ink-soft leading-relaxed line-clamp-3">{p[`description_${lng}`]}</p>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATIONS */}
      <section className="section bg-sand/50">
        <div className="container-x max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <p className="overline mb-3">{t('home.formations_overline')}</p>
              <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t('home.formations_title')}</h2>
            </div>
            <Link to="/formations" className="btn-outline">{t('common.view_all')} <ArrowRight size={16} /></Link>
          </div>
          <Stagger className="grid gap-6 md:grid-cols-3">
            {formations.map((f) => (
              <StaggerItem key={f.id}>
                <div className="bg-white border border-border-soft rounded-3xl overflow-hidden hover-lift h-full">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={resolveImg(f.image)} alt={f[`title_${lng}`]} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-mono text-earth">{f.duration}</span>
                    <h3 className="font-heading text-xl text-ink mt-2 mb-2">{f[`title_${lng}`]}</h3>
                    <p className="text-sm text-ink-soft line-clamp-3">{f[`description_${lng}`]}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* VISIT CTA */}
      <section className="relative h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/v4wboqda_IMG_7096.jpeg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/40 to-transparent" />
        </div>
        <div className="relative container-x max-w-7xl mx-auto">
          <FadeUp>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-bone tracking-tight max-w-2xl text-balance">
              {t('home.cta_visit_title')}
            </h2>
            <p className="mt-6 text-lg text-bone/85 max-w-xl">{t('home.cta_visit_sub')}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/experiences" className="btn-accent" data-testid="cta-visit">
                {t('experiences.cta_explore')} <ArrowRight size={18} />
              </Link>
              <Link to="/support" className="inline-flex items-center gap-2 text-bone hover:text-sun border-b border-bone/40 pb-1 px-1 py-2" data-testid="cta-support">
                {t('nav.support')} <ArrowRight size={16} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
